import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, DialogContent, DialogTitle, Tab } from "@mui/material";
import DialogContext from 'components/confirm-dialog-context';
import CustomDialog from "components/custom/CustomDialog";
import useCatchApiError from "hooks/useCatchApiError";
import useFlowRoles from "hooks/useFlowRoles";
import { useSnackbarCustom } from 'hooks/useSnackCustom';
import HeaderOuBannerDependendoDaInterpretacaoH1 from "layout/header-ou-banner-dependendo-da-interpretacao-h1/HeaderOuBannerDependendoDaInterpretacaoH1";
import DfeService from 'pages/dfe/dfe.service';
import CustomIFrame from 'pages/Venda/Header/CustomIFrame';
import CustomLoading from 'pages/Venda/Header/CustomLoading';
import { useContext, useEffect, useState } from "react";
import { theme } from 'theme';
import ConsignadoService from "../consignado.service";
import { FlowConsignadoImpressaoDTO } from '../impressao/consignado-impressao.model';
import ConsignadoImpressaoService from '../impressao/consignado-impressao.service';
import ConsignadoPDF from '../impressao/ConsignadoPDF';
import { FlowConsignadoVisualizarNotaBaseDTO } from '../movimentacoes/movimentacoes.model';
import VisualizadorNotas, { TiposNotasTabView } from '../visualizador-notas/VisualizadorNotas';
import { AbaCliente } from "./abas/AbaCliente";
import { AbaIdentificacao } from './abas/AbaIdentificacao';
import { AbaPedido } from "./abas/AbaPedido";
import { AbaProduto } from "./abas/AbaProduto";
import { ConsignadoVisualizarDialogProps, initialState, tabContentStyle, tabHeaderStyle, VisualizarTabs, VisualizarTabsDescritivo } from "./consignado-visualizar-dialog.model";

const ConsignadoVisualizarDialog = (props: ConsignadoVisualizarDialogProps) => {
    const { canImprimir } = useFlowRoles().consignado;
    const consignadoService = ConsignadoService();
    const consignadoImpressaoService = ConsignadoImpressaoService();
    const dfeService = DfeService();

    const { throwErrorMessage } = useCatchApiError();
    const [localState, setLocalState] = useState(initialState);
    const [impressao, setImpressao] = useState(null as FlowConsignadoImpressaoDTO | null);
    const [urlDanfe, setUrlDanfe] = useState(null as string | null);
    const dialog = useContext(DialogContext);
    const { addSuccess } = useSnackbarCustom();

    useEffect(() => {
        if (props.open && !!props.uuidConsignado) {
            consultar();
        }
        //eslint-disable-next-line
    }, [props.open, props.uuidConsignado]);

    const consultar = () => {
        consignadoService.visualizar(props.uuidConsignado!)
            .then(({ data }) => {
                setLocalState(prev => ({ ...prev, data, loading: false }));
            })
            .catch(throwErrorMessage);
    };

    const handleClose = () => {
        props.onCloseVisualizarDialog();
        setTimeout(() => {
            setLocalState({
                data: null,
                loading: true,
                screenLoading: false,
                selectedTab: VisualizarTabs.IDENTIFICACAO
            });
        }, 200);
    };

    const getDialogWidth = () => {
        switch (localState.selectedTab) {
            case VisualizarTabs.CLIENTE:
            case VisualizarTabs.IDENTIFICACAO:
                return 'md';
            case VisualizarTabs.PRODUTOS:
            case VisualizarTabs.CONSIGNACOES:
            case VisualizarTabs.DEVOLUCOES:
                return 'xl';
            case VisualizarTabs.PEDIDOS:
                return 'lg';
        }
    };

    const handleImprimirRomaneio = (item: FlowConsignadoVisualizarNotaBaseDTO) => {
        setLocalState(prev => ({ ...prev, screenLoading: true }));
        consignadoImpressaoService.impressao(props.uuidConsignado!, item.chaveAcesso)
            .then(({ data }) => {
                setImpressao(data);
            })
            .finally(() => {
                setLocalState(prev => ({ ...prev, screenLoading: false }));
            });
    };

    const handleImprimirDanfe = (item: FlowConsignadoVisualizarNotaBaseDTO) => {
        setLocalState(prev => ({ ...prev, screenLoading: true }));
        dfeService.danfe(item.chaveAcesso)
            .then(({ data }) => {
                setUrlDanfe(data);
            })
            .finally(() => {
                setTimeout(() => {
                    setLocalState(prev => ({ ...prev, screenLoading: false }));
                }, 200);
            });
    };

    const handleDeleteNota = (item: FlowConsignadoVisualizarNotaBaseDTO) => {
        dialog.confirm({
            error: false,
            title: 'Excluir nota',
            message: `Deseja realmente continuar?`,
            onConfirm: () => {
                setLocalState(prev => ({ ...prev, loading: true, }));
                consignadoService.excluirNota(item.chaveAcesso)
                    .then(() => {
                        addSuccess({
                            id: 'nota-excluida',
                            message: 'Nota excluída com sucesso!',
                            preventDuplicate: true,
                        });
                        consultar();
                    })
                    .catch(err => {
                        throwErrorMessage(err);
                    });
            }
        });
    };

    return <>
        <CustomDialog
            open={props.open}
            fullWidth
            maxWidth={getDialogWidth()}
            onClose={handleClose}
        >
            <DialogTitle
                id="consignado-dialog-title"
                textAlign={'center'} sx={{
                    padding: 0
                }}>
                <HeaderOuBannerDependendoDaInterpretacaoH1
                    id='consignado_visualizar'
                    iconClassName='fa-solid fa-file-invoice-dollar'
                    label='Movimentações'
                />
            </DialogTitle>

            <DialogContent>
                {!localState.loading &&
                    <TabContext
                        value={localState.selectedTab}
                    >
                        <Box sx={{ margin: '20px 0', borderBottom: 1, borderColor: 'divider', width: '100%' }}>
                            <TabList
                                onChange={(_, value) => {
                                    setLocalState(prev => ({
                                        ...prev,
                                        selectedTab: value,
                                    }));
                                }}
                                sx={{
                                    width: '100%',
                                    '& .MuiTabs-scroller .MuiTabs-indicator ': {
                                        backgroundColor: theme.palette.primary.main,
                                    },
                                    '& .MuiTabs-flexContainer': {
                                        display: 'flex',
                                        justifyContent: 'space-around'
                                    }
                                }}
                            >
                                {Object.values(VisualizarTabs)
                                    .filter(s => {
                                        const defaultVisible = [VisualizarTabs.IDENTIFICACAO, VisualizarTabs.CLIENTE, VisualizarTabs.PRODUTOS].includes(s);
                                        const mostraConsignacoes = s === VisualizarTabs.CONSIGNACOES && !!localState.data?.visualizarNotas.notasSaida.length;
                                        const mostraDevolucoes = s === VisualizarTabs.DEVOLUCOES && !!localState.data?.visualizarNotas.notasDevolucao.length;
                                        const mostraPedidos = s === VisualizarTabs.PEDIDOS && !!localState.data?.abaPedidos.pedidos.length;
                                        return defaultVisible || mostraConsignacoes || mostraDevolucoes || mostraPedidos;
                                    })
                                    .map(key => {
                                        const keyCast = key as VisualizarTabs;
                                        return <Tab
                                            sx={tabHeaderStyle}
                                            onClick={(e) => setLocalState(prev => ({ ...prev, selectedTab: keyCast }))}
                                            label={VisualizarTabsDescritivo[keyCast]}
                                            value={keyCast}
                                        />;
                                    })}
                            </TabList>
                        </Box>

                        <TabPanel
                            sx={tabContentStyle}
                            value={VisualizarTabs.IDENTIFICACAO}
                        >
                            <AbaIdentificacao data={localState.data!.abaIdentificacao} />
                        </TabPanel>

                        <TabPanel
                            sx={tabContentStyle}
                            value={VisualizarTabs.CLIENTE}
                        >
                            <AbaCliente data={localState.data!.abaCliente} />
                        </TabPanel>

                        <TabPanel
                            sx={tabContentStyle}
                            value={VisualizarTabs.PRODUTOS}
                        >
                            <AbaProduto data={localState.data!.abaProdutos} />
                        </TabPanel>

                        <TabPanel
                            sx={tabContentStyle}
                            value={VisualizarTabs.CONSIGNACOES}
                        >
                            <VisualizadorNotas
                                data={localState.data!.visualizarNotas.notasSaida}
                                tab={TiposNotasTabView.SAIDA}
                                onImprimir={canImprimir ? handleImprimirRomaneio : undefined}
                                onImprimirDanfe={canImprimir ? handleImprimirDanfe : undefined}
                                onDeleteNota={handleDeleteNota}
                                onReload={consultar}
                                toogleButtonPropsTop='-33px'
                            />
                        </TabPanel>

                        <TabPanel
                            sx={tabContentStyle}
                            value={VisualizarTabs.DEVOLUCOES}
                        >
                            <VisualizadorNotas
                                data={localState.data!.visualizarNotas.notasDevolucao}
                                tab={TiposNotasTabView.ENTRADA}
                                toogleButtonPropsTop='-33px'
                                onImprimir={canImprimir ? handleImprimirRomaneio : undefined}
                                onImprimirDanfe={canImprimir ? handleImprimirDanfe : undefined}
                                onReload={consultar}
                            />
                        </TabPanel>

                        <TabPanel
                            sx={tabContentStyle}
                            value={VisualizarTabs.PEDIDOS}
                        >
                            <AbaPedido data={localState.data!.abaPedidos} />
                        </TabPanel>
                    </TabContext>
                }
            </DialogContent>
        </CustomDialog>
        {!!impressao &&
            <ConsignadoPDF
                dados={impressao}
                onClose={() => {
                    setImpressao(null);
                }}
            />
        }

        {
            !!urlDanfe &&
            <CustomIFrame
                url={urlDanfe}
                onClose={() => {
                    setUrlDanfe(null);
                }}
            />
        }

        {localState.screenLoading && <CustomLoading />}
    </>;
};

export default ConsignadoVisualizarDialog;
